import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'
import { QuoteHeader } from './components/QuoteHeader'
import { QuoteDetails } from './components/QuoteDetails'
import { ProductsPanel } from './components/ProductsPanel'
import { MetricsPanel } from './components/MetricsPanel'
import { PartnershipsPanel } from './components/PartnershipsPanel'
import { NotesPanel } from './components/NotesPanel'
import { QuoteSummary } from './components/QuoteSummary'
import { ReviewBanner } from './components/ReviewBanner'
import { QuoteReviewStatus } from './components/QuoteReviewStatus'
import { useQuoteStore } from './store'
import { useHomeStore } from '@/features/home/store'
import { useAuthStore } from '@store/authStore'
import { dealsApi, quotesApi, draftsApi, scopesApi } from '@api/client'
import { useScopeStore } from '@features/scope/store'
import { mapDbScopeToStoreState } from '@features/scope/utils/mappers'
import { mapDbQuoteToStoreState, mapStoreStateToDbQuote } from './utils/mappers'
import { useAutoSave } from '@hooks/useAutoSave'
import { AutoSaveIndicator } from '@hooks/AutoSaveIndicator'
import { LastEditedIndicator } from '@components/ui/LastEditedIndicator'
import { ChangeHistory } from '@components/ui/ChangeHistory'
import { QuoteVersionsTab } from './components/QuoteVersionsTab'
import { clsx } from 'clsx'
import { FileText, TrendingUp, Handshake, History, Loader2 } from 'lucide-react'

type TabId = 'products' | 'metrics' | 'partnerships' | 'versions'

const TABS = [
  { id: 'products' as TabId, label: 'Products & Line Items', icon: FileText },
  { id: 'metrics' as TabId, label: 'Financial Metrics', icon: TrendingUp },
  { id: 'partnerships' as TabId, label: 'Partnerships', icon: Handshake },
  { id: 'versions' as TabId, label: 'Versions', icon: History },
]

export function Quote() {
  const [activeTab, setActiveTab] = useState<TabId>('products')
  const [searchParams, setSearchParams] = useSearchParams()
  const [isLoadingSession, setIsLoadingSession] = useState(false)
  const [sessionError, setSessionError] = useState<string | null>(null)
  const [dealStatus, setDealStatus] = useState<string>('draft')
  const [dealCustomerName, setDealCustomerName] = useState<string>('')
  const [reviewSubmitter, setReviewSubmitter] = useState<{ email: string; name: string } | null>(null)
  // Post-review lifecycle fields (approved/sent-back) for the QuoteReviewStatus tag + history.
  const [reviewMeta, setReviewMeta] = useState<{
    reviewedBy?: string | null
    reviewedAt?: string | null
    reviewNotes?: string | null
    finalizedBy?: string | null
    finalizedAt?: string | null
    submittedAt?: string | null
  }>({})
  const [lastEditMeta, setLastEditMeta] = useState<{ updatedAt?: string | null; updatedByName?: string | null }>({})
  // Impl-review state loaded from the deal record (PR 3). NULL status → standard flow.
  const [implReview, setImplReview] = useState<{
    status: string | null
    requestedAt: string | null
    requestedBy: string | null
    snapshot: { feeBefore: number | null; feeAfter: number | null; completedAt: string | null; completedByName: string | null } | null
  }>({ status: null, requestedAt: null, requestedBy: null, snapshot: null })

  const sessionId = searchParams.get('session')
  const draftId = searchParams.get('draft')
  const draftIdRef = useRef<string | null>(draftId && draftId !== 'new' ? draftId : null)
  const { details, selectedProducts, partnership, calculations, loadQuoteState, setQuoteLocks } = useQuoteStore()
  const { selectDeal } = useHomeStore()
  const { user } = useAuthStore()

  // Mirror the deal status into the quote store as a lock flag so child
  // components (ProductsPanel, QuoteDetails, MetricsPanel, etc.) can disable
  // their inputs while the quote is under review. Without this lock, a
  // submitter could keep editing after submission, causing the values a
  // reviewer sees in DealHub to drift from the Slack notification snapshot.
  useEffect(() => {
    setQuoteLocks({
      // Finalized quotes are read-only too — the approved version is frozen (and snapshotted
      // in the Versions tab). Editing is re-enabled only after re-opening via "Submit for
      // review again". This also stops autosave from looping against the server's 423 lock.
      all: dealStatus === 'in_review' || dealStatus === 'finalized' || implReview.status === 'awaiting',
      implementationFee: implReview.status === 'reviewed',
    })
  }, [dealStatus, implReview.status, setQuoteLocks])

  // Auto-save: debounce saves to DB as user edits
  const autoSaveData = useMemo(
    () => ({ details, selectedProducts, partnership }),
    [details, selectedProducts, partnership]
  )

  const handleAutoSave = useCallback(async () => {
    if (sessionId) {
      // Deal-based save (existing behaviour)
      const dbQuote = mapStoreStateToDbQuote({ details, selectedProducts, partnership, calculations })
      const response = await quotesApi.save(Number(sessionId), {
        ...dbQuote,
        _user_email: user?.email,
        _user_name: user?.name,
      } as any)
      if (!response.success) {
        throw new Error(response.error || 'Failed to save quote')
      }
    } else if (draftIdRef.current || draftId === 'new') {
      // Draft save
      const payload = {
        data_json: JSON.stringify({ details, selectedProducts, partnership }),
        customer_name: details.customerName || null,
        title: details.customerName || 'Untitled Quote',
        feature_type: 'quote' as const,
        user_email: user?.email,
        user_name: user?.name,
      }
      if (!draftIdRef.current) {
        // First save — create the draft
        const res = await draftsApi.create(payload)
        if (res.success && res.data) {
          draftIdRef.current = String(res.data.id)
          setSearchParams({ draft: String(res.data.id) }, { replace: true })
        } else {
          throw new Error(res.error || 'Failed to create draft')
        }
      } else {
        // Update existing draft
        const res = await draftsApi.update(Number(draftIdRef.current), payload)
        if (!res.success) {
          throw new Error(res.error || 'Failed to save draft')
        }
      }
    }
  }, [sessionId, draftId, details, selectedProducts, partnership, calculations, user, setSearchParams])

  const { status: autoSaveStatus, retryCount, lastError, retry: retrySave } = useAutoSave({
    data: autoSaveData,
    onSave: handleAutoSave,
    // Disable autosave when the quote is locked so any stale debounced
    // changes that haven't flushed yet don't hit the worker's 423 reject.
    enabled: (!!sessionId || !!draftId) && !isLoadingSession && dealStatus !== 'in_review' && dealStatus !== 'finalized' && implReview.status !== 'awaiting',
    debounceMs: 3000,
  })

  // Load session data when ?session= param is present
  const loadSession = useCallback(async () => {
    if (!sessionId) return

    setIsLoadingSession(true)
    setSessionError(null)

    // Create an AbortController for timeout
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000) // 15s timeout

    try {
      // Fetch the deal info (includes relations)
      const dealResponse = await dealsApi.get(Number(sessionId))
      if (!dealResponse.success || !dealResponse.data) {
        setSessionError('Deal not found')
        setIsLoadingSession(false)
        clearTimeout(timeoutId)
        return
      }

      const deal = dealResponse.data
      setDealStatus(deal.status)
      setDealCustomerName(deal.customer_name)
      setImplReview({
        status: deal.impl_review_status ?? null,
        requestedAt: deal.impl_review_requested_at ?? null,
        requestedBy: deal.impl_review_requested_by_name ?? null,
        snapshot: {
          feeBefore: deal.impl_review_fee_before ?? null,
          feeAfter: deal.impl_review_fee_after ?? null,
          completedAt: deal.impl_review_completed_at ?? null,
          completedByName: deal.impl_review_completed_by_name ?? null,
        },
      })
      if (deal.review_submitted_by_email) {
        setReviewSubmitter({ email: deal.review_submitted_by_email, name: deal.review_submitted_by_name || '' })
      }
      setReviewMeta({
        reviewedBy: deal.reviewed_by ?? null,
        reviewedAt: deal.reviewed_at ?? null,
        reviewNotes: deal.review_notes ?? null,
        finalizedBy: deal.finalized_by ?? null,
        finalizedAt: deal.finalized_at ?? null,
        submittedAt: deal.review_submitted_at ?? null,
      })

      // Set the deal context in the home store (so QuoteHeader shows the deal tag)
      selectDeal({
        id: String(deal.id),
        hubspotDealId: deal.hubspot_deal_id,
        name: deal.hubspot_deal_name || deal.customer_name,
        amount: null,
        stage: deal.status,
        closeDate: null,
        ownerName: deal.sales_owner || '',
        companyName: deal.customer_name,
        createdAt: deal.created_at,
      })

      // Fetch the quote data
      const quoteResponse = await quotesApi.getByDealId(Number(sessionId))
      if (quoteResponse.success && quoteResponse.data) {
        const storeState = mapDbQuoteToStoreState(quoteResponse.data, deal.customer_name)
        loadQuoteState({
          details: storeState.details,
          selectedProducts: storeState.selectedProducts,
          partnership: storeState.partnership,
        })
        setLastEditMeta({
          updatedAt: quoteResponse.data.updated_at,
          updatedByName: quoteResponse.data.updated_by_name,
        })

        // Load the linked scope into the scope store so useScopeHours() works.
        // Use scope_source_deal_id if set, otherwise fall back to this deal's scope.
        const scopeDealId = storeState.details.scopeSourceDealId || Number(sessionId)
        try {
          const scopeRes = await scopesApi.getByDealId(scopeDealId)
          if (scopeRes.success && scopeRes.data) {
            const mapped = mapDbScopeToStoreState(scopeRes.data)
            useScopeStore.getState().setScope(mapped)
          }
        } catch {
          // Scope loading is best-effort — quote works without it
        }
      } else {
        // No quote yet — still try to load this deal's scope
        try {
          const scopeRes = await scopesApi.getByDealId(Number(sessionId))
          if (scopeRes.success && scopeRes.data) {
            const mapped = mapDbScopeToStoreState(scopeRes.data)
            useScopeStore.getState().setScope(mapped)
          }
        } catch {
          // best-effort
        }
      }
    } catch (error) {
      console.error('Failed to load session:', error)
      if (error instanceof DOMException && error.name === 'AbortError') {
        setSessionError('Loading timed out — the server may be slow. Please try again.')
      } else {
        setSessionError('Failed to load session data')
      }
    } finally {
      clearTimeout(timeoutId)
      setIsLoadingSession(false)
    }
  }, [sessionId, selectDeal, loadQuoteState])

  useEffect(() => {
    if (sessionId) loadSession()
  }, [sessionId])

  // Load draft data when ?draft= param is present (not 'new')
  useEffect(() => {
    if (!draftId || draftId === 'new' || sessionId) return

    const loadDraft = async () => {
      setIsLoadingSession(true)
      setSessionError(null)
      try {
        const res = await draftsApi.get(Number(draftId))
        if (res.success && res.data) {
          const parsed = JSON.parse(res.data.data_json)
          loadQuoteState(parsed)
          if (res.data.customer_name) {
            setDealCustomerName(res.data.customer_name)
          }
        } else {
          setSessionError('Draft not found')
        }
      } catch (error) {
        console.error('Failed to load draft:', error)
        setSessionError('Failed to load draft data')
      } finally {
        setIsLoadingSession(false)
      }
    }

    loadDraft()
  }, [draftId, sessionId])

  const handleReviewAction = (newStatus: string) => {
    setDealStatus(newStatus)
  }

  if (isLoadingSession) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="text-center">
          <Loader2 size={32} className="animate-spin text-primary mx-auto mb-3" />
          <p className="text-sm text-[var(--text-muted)]">Loading quote session...</p>
        </div>
      </div>
    )
  }

  if (sessionError) {
    return (
      <div className="w-full flex items-center justify-center py-20">
        <div className="text-center">
          <p className="text-sm text-danger mb-2">{sessionError}</p>
          <button
            onClick={loadSession}
            className="mt-2 px-4 py-2 text-sm font-medium text-primary border border-primary/30 rounded-lg hover:bg-primary/10 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full space-y-4">
      {/* Review Banner — standard in_review review, plus the impl-review state machine
          (amber awaiting / emerald reviewed). */}
      {sessionId && (dealStatus === 'in_review' || implReview.status === 'awaiting' || implReview.status === 'reviewed') && (
        <ReviewBanner
          dealId={sessionId}
          customerName={dealCustomerName}
          dealStatus={dealStatus}
          onStatusChange={handleReviewAction}
          submitterEmail={reviewSubmitter?.email}
          submitterName={reviewSubmitter?.name}
          implReviewStatus={implReview.status}
          implReviewRequestedAt={implReview.requestedAt}
          implReviewRequestedBy={implReview.requestedBy}
          implReviewSnapshot={implReview.snapshot}
          onContinued={loadSession}
        />
      )}

      {/* Post-review marker: green "Approved" / amber "Sent back" tag + review history.
          Suppressed while an active review banner is showing (in_review / impl loop). */}
      {sessionId && dealStatus !== 'in_review' && !implReview.status && (
        <QuoteReviewStatus
          status={dealStatus}
          submittedByName={reviewSubmitter?.name}
          submittedAt={reviewMeta.submittedAt}
          implRequestedAt={implReview.requestedAt}
          implCompletedAt={implReview.snapshot?.completedAt}
          implCompletedByName={implReview.snapshot?.completedByName}
          implFeeBefore={implReview.snapshot?.feeBefore}
          implFeeAfter={implReview.snapshot?.feeAfter}
          reviewedBy={reviewMeta.reviewedBy}
          reviewedAt={reviewMeta.reviewedAt}
          reviewNotes={reviewMeta.reviewNotes}
          finalizedBy={reviewMeta.finalizedBy}
          finalizedAt={reviewMeta.finalizedAt}
        />
      )}

      <QuoteHeader onReviewSubmitted={loadSession} implReviewStatus={implReview.status} dealStatus={dealStatus} />

      {/* Auto-save indicator + last edited */}
      {(sessionId || draftId) && (
        <div className="flex items-center justify-between -mt-2">
          <LastEditedIndicator updatedAt={lastEditMeta.updatedAt} updatedByName={lastEditMeta.updatedByName} />
          <AutoSaveIndicator status={autoSaveStatus} retryCount={retryCount} lastError={lastError} onRetry={retrySave} />
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-[var(--bg-secondary)] border border-[var(--border-color)] rounded-lg p-1">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={clsx(
              'flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors',
              activeTab === tab.id
                ? 'bg-primary text-white'
                : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]'
            )}
          >
            <tab.icon size={16} />
            {tab.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_2fr_1fr] gap-4">
        {/* Left Column - Deal Details */}
        <div className="space-y-4">
          <QuoteDetails />
        </div>

        {/* Middle Column - Main Content */}
        <div className="space-y-4">
          {activeTab === 'products' && <ProductsPanel />}
          {activeTab === 'metrics' && <MetricsPanel />}
          {activeTab === 'partnerships' && <PartnershipsPanel />}
          {activeTab === 'versions' && (
            sessionId
              ? <QuoteVersionsTab dealId={Number(sessionId)} />
              : <div className="py-12 text-center text-[var(--text-muted)] text-sm">Versions appear once this quote is linked to a deal and approved.</div>
          )}
        </div>

        {/* Right Column - Summary + Notes + History */}
        <div className="space-y-4">
          <QuoteSummary />
          <NotesPanel />
          {sessionId && <ChangeHistory dealId={Number(sessionId)} table="quotes" />}
        </div>
      </div>
    </div>
  )
}
